/**
 * ai-member-form-lib.ts — Phần THUẦN (không React) của form KẾT NỐI nhân viên AI bên thứ ba / Webhook
 * (ai-member-form-dialog.tsx): danh mục loại/nền tảng, trường kết nối theo nền tảng, trạng thái form ban đầu, kiểm tra và
 * dựng payload gửi `/ai/bots`. Nhân viên AI tích hợp sẵn tạo ở luồng tạo mới, sửa ở màn nhân viên AI; kênh, giờ làm,
 * trực tuyến, số khách/ngày của mọi loại sửa ở mục "Giờ làm & kênh" của màn nhân viên AI.
 */
import type { AiBot, AiBotInput, AiBotKind } from '@/hooks/use-ai'
import type { AiModelEntry } from '@/lib/ai-credits'
import { i18n } from '@/lib/i18n'

/** Ba cách có nhân viên AI — khớp 3 lựa chọn của hộp "Nhân viên AI". */
export type AiMemberMode = 'builtin' | 'third_party' | 'webhook'
/** Hai cách dùng form kết nối. */
export type ConnectMode = Exclude<AiMemberMode, 'builtin'>

export type ThirdPartyKind = Exclude<AiBotKind, 'builtin' | 'webhook'>

export const THIRD_PARTY_KINDS: ThirdPartyKind[] = ['openai_assistant', 'coze_cn', 'coze_intl', 'dify', 'gptbots']

export function modeOfKind(kind: AiBotKind): AiMemberMode {
  if (kind === 'builtin') return 'builtin'
  if (kind === 'webhook') return 'webhook'
  return 'third_party'
}

/** Nhãn loại nhân viên (badge ở bảng nhân viên / thẻ Đội AI). */
export const KIND_BADGE: Record<AiBotKind, string> = {
  get builtin() { return i18n.t('team:aiMemberFormLib.kindBadge.builtin') },
  openai_assistant: 'OpenAI',
  coze_cn: 'Coze',
  coze_intl: 'Coze',
  dify: 'Dify',
  gptbots: 'GPTBots',
  webhook: 'Webhook',
}

export interface PlatformField {
  key: string
  label: string
  placeholder?: string
  required: boolean
  secret: boolean
  /** Ô nhiều dòng + nút "Tải lên tệp PEM". */
  pem?: boolean
  hint?: string
  /**
   * Ô CHỌN model theo đúng danh mục "Key của tôi" của nhà cung cấp này (GET /ai/models?source=byok) — không gõ tự do, để
   * khớp model hệ thống hỗ trợ. Backend (external-config.ts) cũng chỉ nhận model trong danh mục đó.
   */
  modelProvider?: string
}

export interface PlatformDef {
  kind: ThirdPartyKind
  label: string
  /** Tài liệu chính thức (đã đối chiếu) — mở từ link "Xem hướng dẫn". */
  docUrl: string
  fields: PlatformField[]
  note?: string
  /** Đã ngừng cho tạo mới (14/10/2026) — chỉ hiện khi đang sửa nhân viên AI cũ dùng nền tảng này (bot cũ vẫn chạy). */
  legacy?: boolean
}

const COZE_FIELDS: PlatformField[] = [
  { key: 'clientId', label: 'Client ID', required: true, secret: false, get placeholder() { return i18n.t('team:aiMemberFormLib.fields.coze.clientIdPh') } },
  { key: 'publicKeyId', label: 'Public Key', required: true, secret: false, get placeholder() { return i18n.t('team:aiMemberFormLib.fields.coze.publicKeyIdPh') } },
  { key: 'privateKey', label: 'Private Key', required: true, secret: true, pem: true, placeholder: '-----BEGIN PRIVATE KEY-----' },
  { key: 'botId', label: 'Bot ID', required: true, secret: false, get placeholder() { return i18n.t('team:aiMemberFormLib.fields.coze.botIdPh') } },
]

export const PLATFORMS: PlatformDef[] = [
  {
    kind: 'openai_assistant',
    get label() { return i18n.t('team:aiMemberFormLib.platforms.openaiAssistant') },
    docUrl: 'https://platform.openai.com/docs/api-reference/responses/create',
    fields: [
      { key: 'apiKey', label: 'API Key', required: true, secret: true, placeholder: 'sk-...' },
      { key: 'promptId', label: 'Prompt ID', required: false, secret: false, placeholder: 'pmpt_...' },
      {
        key: 'model', required: false, secret: false, modelProvider: 'openai',
        get label() { return i18n.t('team:aiMemberFormLib.fields.openai.modelLabel') },
        get placeholder() { return i18n.t('team:aiMemberFormLib.fields.openai.modelPh') },
      },
    ],
    get note() { return i18n.t('team:aiMemberFormLib.platforms.openaiNote') },
  },
  {
    kind: 'coze_cn',
    legacy: true,
    label: 'Coze CN (coze.cn)',
    docUrl: 'https://www.coze.cn/open/docs/developer_guides/oauth_jwt',
    fields: COZE_FIELDS,
  },
  {
    kind: 'coze_intl',
    legacy: true,
    get label() { return i18n.t('team:aiMemberFormLib.platforms.cozeIntl') },
    docUrl: 'https://www.coze.com/open/docs/developer_guides/oauth_jwt',
    fields: COZE_FIELDS,
  },
  {
    kind: 'dify',
    legacy: true,
    label: 'Dify',
    docUrl: 'https://docs.dify.ai/en/api-reference/chat-messages/send-chat-message',
    fields: [
      { key: 'apiKey', label: 'API Key', required: true, secret: true, placeholder: 'app-...' },
      {
        key: 'baseUrl', required: false, secret: false, placeholder: 'https://api.dify.ai/v1',
        get label() { return i18n.t('team:aiMemberFormLib.fields.dify.baseUrlLabel') },
        get hint() { return i18n.t('team:aiMemberFormLib.fields.dify.baseUrlHint') },
      },
    ],
  },
  {
    kind: 'gptbots',
    legacy: true,
    label: 'GPTBots',
    docUrl: 'https://www.gptbots.ai/docs/api-reference/conversation-api/send-message-v2',
    fields: [
      { key: 'apiKey', label: 'API Key', required: true, secret: true, get placeholder() { return i18n.t('team:aiMemberFormLib.fields.gptbots.apiKeyPh') } },
      {
        key: 'endpoint', required: false, secret: false, placeholder: 'sg',
        get label() { return i18n.t('team:aiMemberFormLib.fields.gptbots.endpointLabel') },
        get hint() { return i18n.t('team:aiMemberFormLib.fields.gptbots.endpointHint') },
      },
    ],
  },
]

/** Giá trị đại diện "không chọn model" trong ô chọn (Select không nhận chuỗi rỗng). */
export const NO_MODEL = '__none__'

/**
 * Lựa chọn model cho ô Model của nền tảng ngoài: đúng các model "Key của tôi" của nhà cung cấp (cùng thứ tự, cùng tên hiển thị
 * với ô chọn model ở Nguồn AI). Model đang lưu mà không còn trong danh mục (bot cũ) → vẫn hiện để không mất giá trị, gắn nhãn.
 */
export function modelOptionsFor(
  models: AiModelEntry[] | null | undefined, provider: string, current: string,
): { value: string; label: string }[] {
  const seen = new Set<string>()
  const opts: { value: string; label: string }[] = []
  for (const m of models ?? []) {
    if (m.provider !== provider || m.source !== 'byok' || seen.has(m.model)) continue
    seen.add(m.model)
    opts.push({ value: m.model, label: m.title })
  }
  const cur = current.trim()
  if (cur && !seen.has(cur)) opts.push({ value: cur, label: i18n.t('team:aiMemberFormLib.modelNoLongerListed', { model: cur }) })
  return opts
}

/**
 * Nền tảng chọn được ở mục "Kết nối bot bên ngoài": Trợ lý OpenAI (bot dựng sẵn bằng Prompt ID); Coze / Dify / GPTBots đã ngừng
 * nên chỉ hiện khi đó là nền tảng của bot đang sửa. Model thuần (Claude, Gemini, Grok, DeepSeek…) đi lối "Tạo bằng Key của tôi".
 */
export function selectablePlatforms(currentKind?: AiBotKind | null): PlatformDef[] {
  return PLATFORMS.filter((p) => !p.legacy || p.kind === currentKind)
}

/** Giá trị ô "Nền tảng" đại diện cho Webhook (bot tự dựng) — gộp chung một ô với các nền tảng bot. */
export const WEBHOOK_OPTION = 'webhook'

export function platformOf(kind: AiBotKind): PlatformDef | undefined {
  return PLATFORMS.find((p) => p.kind === kind)
}

/** Chuỗi bí mật đã che do backend trả về ("••••abcd"). */
export const MASK_PREFIX = '••••'
export const isMasked = (v: string) => v.startsWith(MASK_PREFIX)

export interface AiMemberFormState {
  mode: ConnectMode
  name: string
  avatarEmoji: string
  avatarUrl: string
  teamId: string | null
  // third_party
  kind: ThirdPartyKind
  externalConfig: Record<string, string>
  // webhook
  webhookReceiveUrl: string
}

export const NAME_MAX = 50

export function initialFormState(mode: ConnectMode, bot?: AiBot | null): AiMemberFormState {
  const kind = bot && bot.kind !== 'builtin' && bot.kind !== 'webhook' ? bot.kind : 'openai_assistant'
  const botMode = bot ? modeOfKind(bot.kind) : mode
  return {
    mode: botMode === 'builtin' ? mode : botMode,
    name: bot?.name ?? '',
    avatarEmoji: bot?.avatarEmoji ?? '🤖',
    avatarUrl: bot?.avatarUrl ?? '',
    teamId: bot?.teamId ?? null,
    kind,
    externalConfig: { ...(bot?.externalConfig ?? {}) },
    webhookReceiveUrl: bot?.webhookReceiveUrl ?? '',
  }
}

/** Lỗi theo tên trường (tiếng Việt). Rỗng = hợp lệ. */
export function validateForm(s: AiMemberFormState, isEdit: boolean): Record<string, string> {
  const errors: Record<string, string> = {}
  const name = s.name.trim()
  if (!name) errors.name = i18n.t('team:aiMemberFormLib.errors.nameRequired')
  else if (name.length > NAME_MAX) errors.name = i18n.t('team:aiMemberFormLib.errors.nameTooLong', { max: NAME_MAX })
  if (s.avatarUrl.trim() && !/^(https?:\/\/|\/uploads\/)/i.test(s.avatarUrl.trim())) {
    errors.avatarUrl = i18n.t('team:aiMemberFormLib.errors.avatarUrlInvalid')
  }
  if (s.mode === 'third_party') {
    const def = platformOf(s.kind)
    for (const f of def?.fields ?? []) {
      const v = (s.externalConfig[f.key] ?? '').trim()
      // Khi sửa, bí mật để trống/đã che = giữ khoá cũ trên máy chủ.
      if (f.required && !v && !(isEdit && f.secret)) errors[`cfg.${f.key}`] = i18n.t('team:aiMemberFormLib.errors.fieldRequired', { field: f.label })
      if (f.pem && v && !isMasked(v) && !/-----BEGIN (RSA )?PRIVATE KEY-----/.test(v)) {
        errors[`cfg.${f.key}`] = i18n.t('team:aiMemberFormLib.errors.pemInvalid')
      }
    }
    if (s.kind === 'openai_assistant' && !s.externalConfig.promptId?.trim() && !s.externalConfig.model?.trim()) {
      errors['cfg.promptId'] = i18n.t('team:aiMemberFormLib.errors.promptOrModelRequired')
    }
  }
  if (s.mode === 'webhook') {
    const u = s.webhookReceiveUrl.trim()
    if (!u) errors.webhookReceiveUrl = i18n.t('team:aiMemberFormLib.errors.webhookUrlRequired')
    else if (!/^https?:\/\/[^\s]+$/i.test(u)) errors.webhookReceiveUrl = i18n.t('team:aiMemberFormLib.errors.webhookUrlInvalid')
  }
  return errors
}

/** Chỉ giữ trường của nền tảng đang chọn, cắt khoảng trắng; bí mật đã che gửi nguyên để backend giữ khoá cũ. */
export function cleanExternalConfig(kind: ThirdPartyKind, cfg: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of platformOf(kind)?.fields ?? []) {
    const v = (cfg[f.key] ?? '').trim()
    if (v) out[f.key] = v
  }
  return out
}

/** Payload gửi POST/PUT /ai/bots — chỉ thông tin chung + kết nối (vận hành sửa ở màn nhân viên AI). */
export function buildPayload(s: AiMemberFormState): AiBotInput {
  const base: AiBotInput = {
    name: s.name.trim(),
    avatarEmoji: s.avatarEmoji || null,
    avatarUrl: s.avatarUrl.trim() || null,
    teamId: s.teamId,
  }
  if (s.mode === 'webhook') return { ...base, kind: 'webhook', webhookReceiveUrl: s.webhookReceiveUrl.trim() }
  return { ...base, kind: s.kind, externalConfig: cleanExternalConfig(s.kind, s.externalConfig) }
}
